/*
 * SCR-002 Searcher results controller — Phase A1. Reads and writes only through NcApp.gateway() (the mock in A1):
 * searchProducts (query, filters, sort, page; facets come back with the result), getRecommendations + getProduct (trends and
 * recently viewed), setFavorite (product hearts), createUpload + visualSearch (image search), getAsset (thumbnails; no src until
 * real assets exist, so slots stay neutral). Cards are cloned from the <template> elements the section ships, so the markup is the
 * real snippets.
 *
 * State lives here (UNKNOWN: URL serialization of q / filters / sort / page is not specified, so there is no history.pushState):
 *   { q, category[], filters {color[], size[], brand[], price, available}, sort, page, visual }.
 * Any change of query, category, filters or sort reloads page 1; pagination reloads the chosen page and moves focus to the results
 * heading. A request sequence guards against stale responses (a slow older request never overwrites a newer one). "Mostrando a–b de N"
 * is a polite live region. States: loading (skeleton) -> results | empty (filtered: one CTA "Borrar filtros"; plain: one CTA "Probar
 * otra búsqueda") | error (network, server, permission; each with "Reintentar"). Nothing stays loading or blank (AC-012).
 * Navigation (open a product, "Ver más", "Ver todo", visual search route) is Phase B: events only (`nc-search:open-product`,
 * `nc-search:action`, `nc-search:visual-search`, cancelable). Pure helpers are exported for node:test.
 */
(function () {
  var FILTER_KEYS = ['color', 'size', 'brand'];

  function emptyFilters() { return { color: [], size: [], brand: [], price: '', available: false }; }

  // "color:Beige|size:M|available" -> filters; repeated keys add values; unknown keys are ignored
  function parseFilterSeed(text) {
    var f = emptyFilters();
    String(text || '').split('|').forEach(function (part) {
      var bits = part.split(':');
      var key = bits[0].trim();
      var value = bits.slice(1).join(':').trim();
      if (FILTER_KEYS.indexOf(key) >= 0) { if (value && f[key].indexOf(value) < 0) f[key].push(value); }
      else if (key === 'price') f.price = value;
      else if (key === 'available') f.available = value !== '0' && value !== 'false';
    });
    return f;
  }

  function countApplied(f) {
    f = f || emptyFilters();
    return f.color.length + f.size.length + f.brand.length + (f.price ? 1 : 0) + (f.available ? 1 : 0);
  }

  function totalPages(total, pageSize) { return total > 0 && pageSize > 0 ? Math.ceil(total / pageSize) : 0; }

  // 1-based inclusive range of the items on a page, clamped to the total
  function pageRange(page, pageSize, total) {
    if (!total) return { from: 0, to: 0 };
    var from = (page - 1) * pageSize + 1;
    return { from: Math.min(from, total), to: Math.min(page * pageSize, total) };
  }

  function fill(pattern, values) {
    return String(pattern || '').replace(/\{(\w+)\}/g, function (m, k) { return values[k] !== undefined ? values[k] : m; });
  }

  function countText(n, one, other) { return fill(n === 1 ? one : other, { n: n }); }

  function labelOf(list, value) {
    var hit = (list || []).filter(function (x) { return x.value === value; })[0];
    return hit ? hit.label : value;
  }

  // the chips of the applied row, in a stable order; each carries what removing it means
  function appliedChips(f, facets, patterns) {
    f = f || emptyFilters();
    facets = facets || {};
    var out = [];
    FILTER_KEYS.forEach(function (k) {
      f[k].forEach(function (v) { out.push({ key: k, value: v, label: fill(patterns[k], { value: labelOf(facets[k], v) }) }); });
    });
    if (f.price) out.push({ key: 'price', value: f.price, label: fill(patterns.price, { value: labelOf(facets.price, f.price) }) });
    if (f.available) out.push({ key: 'available', value: 'available', label: fill(patterns.available, {}) });
    return out;
  }

  function removeFilter(f, key, value) {
    var n = { color: f.color.slice(), size: f.size.slice(), brand: f.brand.slice(), price: f.price, available: f.available };
    if (FILTER_KEYS.indexOf(key) >= 0) n[key] = n[key].filter(function (v) { return v !== value; });
    else if (key === 'price') n.price = '';
    else if (key === 'available') n.available = false;
    return n;
  }

  // the gateway input for the current state
  function buildInput(state) {
    var f = state.filters;
    return {
      query: state.q,
      filters: { category: state.category.slice(), color: f.color.slice(), size: f.size.slice(), brand: f.brand.slice(), price: f.price, available: f.available },
      sort: state.sort,
      page: state.page
    };
  }

  // which of the three error panels to show
  function errorBlock(err) {
    var kind = err && err.kind;
    if (kind === 'network') return 'error-network';
    if (kind === 'unauthenticated' || kind === 'forbidden') return 'error-permission';
    return 'error-server';
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
      emptyFilters: emptyFilters, parseFilterSeed: parseFilterSeed, countApplied: countApplied, totalPages: totalPages, pageRange: pageRange,
      fill: fill, countText: countText, appliedChips: appliedChips, removeFilter: removeFilter, buildInput: buildInput, errorBlock: errorBlock
    };
  }
  if (typeof customElements === 'undefined' || customElements.get('nc-search-screen')) return;

  function noop() {}

  class NcSearchScreen extends HTMLElement {
    connectedCallback() {
      var self = this;
      this.gw = window.NcApp.gateway();
      this.q = function (sel) { return self.querySelector(sel); };
      this.form = this.q('[data-search-form]');
      this.input = this.q('#search-q');
      this.titleEl = this.q('[data-search-title]');
      this.totalEl = this.q('[data-search-total]');
      this.shownEl = this.q('[data-search-shown]');
      this.grid = this.q('[data-search-grid]');
      this.categoriesEl = this.q('[data-search-categories]');
      this.applied = this.q('[data-applied]');
      this.pagination = this.q('nc-pagination');
      this.panel = this.q('nc-filter-panel');
      this.filtersBtn = this.q('[data-filters-open]');
      this.badge = this.q('[data-filters-badge]');
      this.fileInput = this.q('[data-search-file]');
      this.real = this.getAttribute('data-real') || '';
      this.controlsEl = this.q('.nc-search__controls');
      this.facets = {};
      this.renderedCategories = null;
      this.state = {
        q: (this.getAttribute('data-initial-query') || '').trim(),
        category: (this.getAttribute('data-initial-category') || '').split('|').map(function (x) { return x.trim(); }).filter(Boolean),
        filters: parseFilterSeed(this.getAttribute('data-initial-filters')),
        sort: 'relevance', page: 1, pageSize: 15, total: 0, visual: null
      };
      this.req = 0; // request sequence: a stale response is ignored
      this.favReq = {};
      this.form.addEventListener('submit', this.onSubmit.bind(this));
      this.addEventListener('nc-select:change', this.onSort.bind(this));
      this.addEventListener('nc-chip:toggle', this.onCategory.bind(this));
      this.addEventListener('nc-chip:remove', this.onRemoveChip.bind(this));
      this.addEventListener('nc-pagination:change', this.onPage.bind(this));
      this.addEventListener('nc-filters:apply', this.onApplyFilters.bind(this));
      this.addEventListener('nc-card:toggle', this.onFavorite.bind(this));
      this.addEventListener('click', this.onClick.bind(this));
      this.filtersBtn.addEventListener('click', function () { self.panel.setFacets(self.facets); self.panel.setSelected(self.state.filters); self.panel.open(self.filtersBtn); });
      // the body of an applied chip ("Color: Beige") opens the filters panel to change it; only its × removes it (UX batida UX-20)
      this.addEventListener('click', function (event) {
        var main = event.target.closest('.nc-chip--applied .nc-chip__main');
        if (!main || !self.contains(main)) return;
        self.panel.setFacets(self.facets); self.panel.setSelected(self.state.filters); self.panel.open(main);
      });
      if (this.fileInput) this.fileInput.addEventListener('change', this.onFile.bind(this));
      this.syncControls();
      // the pagination and the filters panel are custom elements defined by their own scripts, which may run after this one
      Promise.all(['nc-pagination', 'nc-filter-panel'].map(function (n) { return customElements.whenDefined(n); })).then(function () {
        self.load();
        self.loadDiscovery();
      });
    }

    // ---- small helpers ----
    attr(name, fallback) { var v = this.getAttribute(name); return v === null || v === '' ? (fallback || '') : v; }

    show(name) {
      ['skeleton', 'idle', 'empty-collection', 'empty-filtered', 'empty-query', 'error-network', 'error-server', 'error-permission'].forEach(function (n) {
        var el = this.q('[data-block="' + n + '"]');
        if (el) el.hidden = n !== name;
      }, this);
      var results = name === 'results';
      this.grid.hidden = !results;
      if (!results) this.pagination.hidden = true;
    }

    // keep focus alive when the control that holds it is about to be hidden
    // `had` = focus was inside the results (grid or pagination) when the load started; no focus move on the first load
    rescue(fallback, had) {
      if (!had) return;
      var active = document.activeElement;
      if (active && active !== document.body && !active.closest('[hidden]') && this.contains(active)) return;
      if (active && active !== document.body && !this.contains(active)) return;
      if (fallback) { if (!fallback.hasAttribute('tabindex') && !/^(A|BUTTON|INPUT)$/.test(fallback.tagName)) fallback.setAttribute('tabindex', '-1'); fallback.focus({ preventScroll: true }); }
    }

    focusTitle() { this.titleEl.focus(); }

    // ---- summary, badge, chips ----
    syncControls() {
      var s = this.state;
      var n = countApplied(s.filters);
      this.badge.textContent = n ? String(n) : '';
      this.badge.hidden = !n;
      this.filtersBtn.setAttribute('aria-label', n ? fill(this.attr('data-filters-aria'), { n: n }) : this.attr('data-filters-aria-none'));
      this.renderTitle();
      this.renderApplied();
    }

    renderTitle() {
      var s = this.state;
      var t = this.titleEl;
      t.textContent = '';
      if (s.visual) { t.textContent = this.attr('data-title-visual'); return; }
      if (this.real === 'collection') { t.textContent = this.attr('data-title-fixed'); return; }
      if (this.real === 'search' && !s.q) { t.textContent = this.attr('data-title-idle'); return; }
      if (!s.q) { t.textContent = this.attr('data-title-empty'); return; }
      var parts = this.attr('data-title-pattern').split('{query}');
      t.appendChild(document.createTextNode(parts[0]));
      var q = document.createElement('span');
      q.className = 'nc-search__query';
      q.textContent = s.q;
      t.appendChild(q);
      t.appendChild(document.createTextNode(parts.slice(1).join('{query}')));
    }

    renderApplied() {
      var self = this;
      var s = this.state;
      var chips = [];
      if (s.visual) chips.push({ key: 'visual', value: s.visual.name, label: fill(this.attr('data-chip-visual'), { value: s.visual.name }) });
      chips = chips.concat(appliedChips(s.filters, this.facets, {
        color: this.attr('data-chip-color'), size: this.attr('data-chip-size'), brand: this.attr('data-chip-brand'),
        price: this.attr('data-chip-price'), available: this.attr('data-chip-available')
      }));
      var list = this.applied.querySelector('.nc-applied-filters__list');
      var tpl = this.applied.querySelector('template[data-tpl="chip"]');
      // rebuilding the row must not drop keyboard focus: remember the focused chip and restore it (by key + value) afterwards
      var focused = document.activeElement && this.applied.contains(document.activeElement) ? document.activeElement.closest('nc-chip') : null;
      var focusKey = focused ? focused.getAttribute('data-key') + '\u0000' + focused.getAttribute('data-value') : null;
      list.textContent = '';
      chips.forEach(function (c) {
        var li = tpl.content.querySelector('li').cloneNode(true);
        var chip = li.querySelector('nc-chip');
        chip.setAttribute('data-key', c.key);
        chip.setAttribute('data-value', c.value);
        li.querySelector('.nc-chip__main').textContent = c.label;
        li.querySelector('.nc-chip__remove').setAttribute('aria-label', fill(self.attr('data-remove-pattern'), { label: c.label }));
        list.appendChild(li);
      });
      if (focusKey) {
        var again = Array.prototype.filter.call(list.querySelectorAll('nc-chip'), function (c) { return c.getAttribute('data-key') + '\u0000' + c.getAttribute('data-value') === focusKey; })[0];
        if (again) again.querySelector('.nc-chip__main').focus({ preventScroll: true });
      }
      this.applied.hidden = !chips.length;
      this.applied.querySelector('[data-applied-clear]').hidden = !countApplied(s.filters);
    }

    renderCategories(facet) {
      var self = this;
      var list = facet || [];
      var key = list.map(function (c) { return c.value; }).join('|');
      if (key === this.renderedCategories) { this.syncCategoryState(); return; }
      var hadFocus = this.categoriesEl.contains(document.activeElement) ? document.activeElement.closest('nc-chip').getAttribute('data-value') : null;
      var tpl = this.q('template[data-tpl="category"]');
      this.categoriesEl.textContent = '';
      list.forEach(function (c) {
        var li = tpl.content.querySelector('li').cloneNode(true);
        var chip = li.querySelector('nc-chip');
        chip.setAttribute('data-value', c.value);
        li.querySelector('.nc-chip__main').textContent = c.label;
        self.categoriesEl.appendChild(li);
      });
      this.renderedCategories = key;
      this.categoriesEl.hidden = !list.length;
      this.syncCategoryState();
      if (hadFocus) { var again = this.categoriesEl.querySelector('nc-chip[data-value="' + hadFocus + '"] .nc-chip__main'); if (again) again.focus({ preventScroll: true }); }
    }

    syncCategoryState() {
      var sel = this.state.category;
      Array.prototype.forEach.call(this.categoriesEl.querySelectorAll('nc-chip'), function (chip) {
        chip.querySelector('.nc-chip__main').setAttribute('aria-pressed', sel.indexOf(chip.getAttribute('data-value')) >= 0 ? 'true' : 'false');
      });
    }

    // ---- loading ----
    load(opts) {
      var self = this;
      opts = opts || {};
      var s = this.state;
      var seq = ++this.req;
      var wasInResults = this.contains(document.activeElement) && !!document.activeElement.closest('[data-search-grid], nc-pagination');
      this.grid.setAttribute('aria-busy', 'true');
      this.pagination.setBusy(true);
      this.shownEl.textContent = this.attr('data-shown-loading');
      if (!this.grid.hidden && this.grid.children.length) {
        // keep the previous cards while the next page arrives (no layout jump); the skeleton only replaces a blank surface
      } else {
        this.show('skeleton');
      }
      var request = s.visual
        ? this.gw.visualSearch({ uploadId: s.visual.uploadId, page: s.page })
        : this.gw.searchProducts(buildInput(s));
      return request.then(function (res) {
        if (seq !== self.req) return;
        self.grid.removeAttribute('aria-busy');
        self.pagination.setBusy(false);
        s.total = res.total;
        s.pageSize = res.pageSize || s.pageSize;
        s.page = res.page || s.page;
        if (res.facets) {
          self.facets = res.facets;
          self.renderCategories(res.facets.category);
          self.panel.setFacets(res.facets);
        }
        self.syncControls();
        self.totalEl.textContent = countText(res.total, self.attr('data-count-one'), self.attr('data-count-other'));
        if (!res.total) {
          var filtered = countApplied(s.filters) > 0 || s.category.length > 0;
          var idle = self.real === 'search' && !s.q && !filtered;
          var none = filtered && !s.visual ? 'empty-filtered' : self.real === 'collection' ? 'empty-collection' : idle ? 'idle' : 'empty-query';
          self.show(none);
          if (idle || (self.real && !filtered && none !== 'empty-query')) self.totalEl.textContent = '';
          if (self.controlsEl) self.controlsEl.hidden = idle;
          self.shownEl.textContent = self.attr('data-shown-empty');
          self.rescue(self.titleEl, wasInResults);
          return;
        }
        if (self.controlsEl) self.controlsEl.hidden = false;
        self.renderCards(res.items);
        self.show('results');
        var r = pageRange(s.page, s.pageSize, s.total);
        self.shownEl.textContent = fill(self.attr('data-shown-pattern'), { from: r.from, to: r.to, total: s.total });
        self.pagination.setPage(s.page, totalPages(s.total, s.pageSize));
        if (opts.focusTitle) self.focusTitle();
        else if (wasInResults) self.rescue(self.titleEl, wasInResults);
      }, function (err) {
        if (seq !== self.req) return;
        self.grid.removeAttribute('aria-busy');
        self.pagination.setBusy(false);
        self.syncControls();
        self.totalEl.textContent = '';
        self.show(errorBlock(err));
        self.shownEl.textContent = self.attr('data-shown-error');
        self.rescue(self.titleEl, wasInResults);
      });
    }

    renderCards(items) {
      var self = this;
      var tpl = this.q('template[data-tpl="card"]');
      this.grid.textContent = '';
      items.forEach(function (item, index) {
        var li = tpl.content.querySelector('li').cloneNode(true);
        var card = li.querySelector('.nc-product-card');
        card.setAttribute('data-id', item.id);
        card.querySelector('.nc-product-card__brand').textContent = item.brand;
        // analytics: the card carries its brand slug so item_impression has a brand (a card without it biases M04 and M12)
        if ((item.brandInfo && item.brandInfo.slug) || item.brandSlug) card.setAttribute('data-nc-brand', (item.brandInfo && item.brandInfo.slug) || item.brandSlug);
        var link = card.querySelector('.nc-card__link');
        link.textContent = item.title;
        link.setAttribute('data-id', item.id);
        if (item.url) link.setAttribute('href', item.url);
        card.querySelector('.nc-product-card__price').textContent = item.price;
        var fav = card.querySelector('.nc-toggle--favorite');
        fav.setAttribute('data-id', item.id);
        fav.setAttribute('aria-pressed', item.favorite ? 'true' : 'false');
        fav.setAttribute('aria-label', fav.getAttribute('aria-label').replace('__TITLE__', item.title));
        var swatches = Array.prototype.slice.call(card.querySelectorAll('.nc-product-card__swatch'));
        swatches.forEach(function (sw, i) {
          if (item.colors && item.colors[i]) sw.style.background = item.colors[i];
          else sw.remove();
        });
        var wrap = card.querySelector('.nc-product-card__swatches');
        if (wrap && !wrap.children.length) wrap.remove();
        self.grid.appendChild(li);
        self.applyAsset(card.querySelector('.nc-card__media'), item.assetId, item.title, index < 5);
      });
    }

    // images enter only through the gateway's asset records; no src (placeholder) leaves the neutral media slot
    applyAsset(slot, assetId, alt, eager) {
      if (!slot || !assetId || !this.gw.getAsset) return;
      this.gw.getAsset(assetId).then(function (a) {
        if (!a || !a.src || !slot.isConnected) return;
        var img = document.createElement('img');
        // the store records carry 1200 px images (product page); a card needs about 640 (2x of ~300 css px)
        img.src = String(a.src).indexOf('cdn.shopify.com') >= 0 ? a.src.replace(/([?&])width=\d+/, '$1width=640') : a.src;
        if (a.width) img.width = a.width;
        if (a.height) img.height = a.height;
        img.alt = a.alt || alt || '';
        img.loading = eager ? 'eager' : 'lazy';
        slot.insertBefore(img, slot.firstChild);
      }, noop);
    }

    // ---- discovery (trends, recently viewed) ----
    loadDiscovery() {
      var self = this;
      var wide = window.matchMedia ? window.matchMedia('(min-width: 1280px)') : null;
      var start = function () {
        ['trends', 'recent'].forEach(function (kind) { self.fillDiscovery(kind); });
      };
      if (!wide || wide.matches) start();
      else if (wide.addEventListener) {
        var once = function (e) { if (e.matches) { wide.removeEventListener('change', once); start(); } };
        wide.addEventListener('change', once);
      }
    }

    fillDiscovery(kind) {
      var self = this;
      var panel = this.q('[data-discovery="' + kind + '"]');
      if (!panel) return;
      var surface = kind === 'trends' ? 'trends' : 'recently-viewed';
      var state = function (name) {
        Array.prototype.forEach.call(panel.querySelectorAll('[data-discovery-state]'), function (el) { el.hidden = el.getAttribute('data-discovery-state') !== name; });
        panel.querySelector('[data-discovery-list]').hidden = name !== 'list';
      };
      state('loading');
      panel.discoverySeq = (panel.discoverySeq || 0) + 1;
      var seq = panel.discoverySeq;
      this.gw.getRecommendations({ surface: surface, page: 1 }).then(function (rec) {
        return Promise.all(rec.items.map(function (r) { return self.gw.getProduct(r.entityId); }));
      }).then(function (products) {
        if (seq !== panel.discoverySeq) return;
        if (!products.length) { state('empty'); return; }
        var list = panel.querySelector('[data-discovery-list]');
        var tpl = panel.querySelector('template[data-tpl="row"]');
        list.textContent = '';
        products.forEach(function (p) {
          var li = tpl.content.querySelector('li').cloneNode(true);
          var btn = li.querySelector('button');
          btn.setAttribute('data-id', p.id);
          var detail = kind === 'trends' ? p.brand : p.price;
          var title = li.querySelector('[data-row-title]');
          if (title) title.textContent = p.title;
          li.querySelector('[data-row-detail]').textContent = detail;
          btn.setAttribute('aria-label', fill(panel.getAttribute('data-row-label'), { title: p.title, detail: detail }));
          list.appendChild(li);
          self.applyAsset(li.querySelector('[data-thumb]'), p.assetId, '', false);
        });
        state('list');
      }, function () {
        if (seq !== panel.discoverySeq) return;
        state('error');
      });
    }

    // ---- events ----
    onSubmit(event) {
      // real mode: a plain GET to /search, the page reloads with the query in the URL
      if (this.real) return;
      event.preventDefault();
      var value = (this.input.value || '').trim();
      this.state.q = value;
      this.state.visual = null;
      this.state.page = 1;
      this.syncControls();
      this.load();
    }

    onSort(event) {
      var input = event.target.querySelector && event.target.querySelector('input[type="hidden"]');
      if (!input || input.name !== 'sort') return;
      var v = event.detail && event.detail.value;
      this.state.sort = v === 'price-asc' || v === 'price-desc' ? v : 'relevance';
      this.state.visual = null;
      this.state.page = 1;
      this.load();
    }

    onCategory(event) {
      var chip = event.target.closest && event.target.closest('nc-chip');
      if (!chip || !this.categoriesEl.contains(chip)) return;
      var value = chip.getAttribute('data-value');
      var on = !!(event.detail && event.detail.pressed);
      var list = this.state.category.filter(function (v) { return v !== value; });
      if (on) list.push(value);
      this.state.category = list;
      this.state.visual = null;
      this.state.page = 1;
      this.syncControls();
      this.load();
    }

    onApplyFilters(event) {
      this.state.filters = event.detail.filters;
      this.state.visual = null;
      this.state.page = 1;
      this.syncControls();
      this.load({ focusTitle: true });
    }

    onRemoveChip(event) {
      var chip = event.target.closest && event.target.closest('nc-chip');
      if (!chip || !this.applied.contains(chip)) return;
      event.preventDefault(); // the controller re-renders the row; the chip must not remove itself
      var items = Array.prototype.slice.call(this.applied.querySelectorAll('nc-chip'));
      var index = items.indexOf(chip);
      var key = chip.getAttribute('data-key');
      if (key === 'visual') this.state.visual = null;
      else this.state.filters = removeFilter(this.state.filters, key, chip.getAttribute('data-value'));
      this.state.page = 1;
      this.syncControls();
      // focus moves to the next chip, else the previous one, else the filters button (never lost with the removed chip)
      var left = Array.prototype.slice.call(this.applied.querySelectorAll('nc-chip'));
      var target = left[index] || left[index - 1];
      (target ? target.querySelector('.nc-chip__main') : this.filtersBtn).focus();
      this.load();
    }

    onPage(event) {
      var p = event.detail.page;
      if (!p || p === this.state.page) return;
      this.state.page = p;
      this.load({ focusTitle: true });
    }

    onClick(event) {
      var t = event.target;
      if (t.closest('[data-applied-clear]')) {
        this.state.filters = emptyFilters();
        this.state.page = 1;
        this.syncControls();
        this.filtersBtn.focus();
        this.load();
        return;
      }
      var block = t.closest('[data-block]');
      if (block && t.closest('[data-nc-retry] .nc-button')) {
        var name = block.getAttribute('data-block');
        if (name === 'empty-filtered') {
          this.state.filters = emptyFilters();
          this.state.category = [];
          this.state.page = 1;
          this.syncControls();
          this.renderCategories(this.facets.category);
          this.load({ focusTitle: true });
        } else if (name === 'empty-query' || name === 'idle' || name === 'empty-collection') {
          this.input.focus();
        } else if (name.indexOf('error-') === 0) {
          this.load({ focusTitle: true });
        }
        return;
      }
      if (t.closest('[data-help-action] .nc-button')) { this.onVisualRequest(); return; }
      var link = t.closest('.nc-card__link');
      // a real product keeps its own link (/products/<handle>): middle click, copy link and prefetch work
      if (link && this.real && link.getAttribute('href') && link.getAttribute('href') !== '#') return;
      if (link) {
        event.preventDefault();
        this.dispatchEvent(new CustomEvent('nc-search:open-product', { bubbles: true, detail: { id: link.getAttribute('data-id') } }));
        return;
      }
      var more = t.closest('[data-discovery-more]');
      if (more) {
        this.dispatchEvent(new CustomEvent('nc-search:action', { bubbles: true, detail: { action: more.closest('[data-discovery]').getAttribute('data-discovery') + '-more' } }));
        return;
      }
      var retry = t.closest('[data-discovery-retry]');
      if (retry) { this.fillDiscovery(retry.closest('[data-discovery]').getAttribute('data-discovery')); return; }
      var row = t.closest('.nc-discovery__row, .nc-discovery__tile');
      if (row) this.dispatchEvent(new CustomEvent('nc-search:open-product', { bubbles: true, detail: { id: row.getAttribute('data-id') } }));
    }

    // ---- favorites (INT-003): optimistic, loading in place, rejection restores the state and says "No se guardó" ----
    onFavorite(event) {
      var d = event.detail;
      if (d.kind !== 'favorite') return;
      var self = this;
      var btn = event.target.closest('.nc-toggle');
      var seq = (this.favReq[d.id] = (this.favReq[d.id] || 0) + 1);
      window.NcCard.setState(btn, 'loading');
      this.gw.setFavorite({ entity: 'product', id: d.id, value: d.pressed }).then(function () {
        if (seq !== self.favReq[d.id]) return;
        window.NcCard.setState(btn, 'default');
      }, function () {
        if (seq !== self.favReq[d.id]) return;
        window.NcCard.setState(btn, 'error', self.attr('data-msg-favorite-error'));
      });
    }

    // ---- visual search: the route is Phase B (event); the file goes through createUpload + visualSearch as a mock seed ----
    onVisualRequest() {
      var ok = this.dispatchEvent(new CustomEvent('nc-search:visual-search', { bubbles: true, cancelable: true, detail: {} }));
      if (!ok) return;
      if (this.fileInput) this.fileInput.click();
      else if (window.NcNav && window.NcNav.notice) window.NcNav.notice(); // real mode: visual search is Phase B
    }

    onFile() {
      var self = this;
      var file = this.fileInput.files && this.fileInput.files[0];
      if (!file) return;
      this.fileInput.value = '';
      var seq = ++this.req;
      this.shownEl.textContent = this.attr('data-shown-loading');
      this.gw.createUpload({ file: file }).then(function (up) {
        if (seq !== self.req) return null;
        self.state.visual = { uploadId: up.id, name: file.name };
        self.state.page = 1;
        self.syncControls();
        return self.load({ focusTitle: true });
      }, function (err) {
        if (seq !== self.req) return;
        var detail = err && err.fieldErrors && (err.fieldErrors.file || err.fieldErrors._) || (err && err.message) || '';
        window.NcToast.show({ tone: 'error', message: self.attr('data-msg-visual-error'), detail: detail });
        self.load();
      });
    }
  }

  customElements.define('nc-search-screen', NcSearchScreen);
})();
